import { createContext, useContext, useState, type ReactNode } from 'react';
import type { PlanTier } from '@/lib/stripe';

export interface ViewAsOverrides {
  /** If set, overrides the subscription tier */
  tier?: PlanTier;
  /** If set, overrides isStaff to false */
  simulateRegularUser?: boolean;
  /** If set, simulates specific course access grants (course IDs) */
  grantedCourseIds?: string[];
}

interface ViewAsContextType {
  active: boolean;
  overrides: ViewAsOverrides;
  setOverrides: (overrides: ViewAsOverrides) => void;
  reset: () => void;
}

const ViewAsContext = createContext<ViewAsContextType>({
  active: false,
  overrides: {},
  setOverrides: () => {},
  reset: () => {},
});

export const ViewAsProvider = ({ children }: { children: ReactNode }) => {
  const [overrides, setOverridesState] = useState<ViewAsOverrides>({});

  const active = !!(overrides.tier || overrides.simulateRegularUser || overrides.grantedCourseIds?.length);

  const setOverrides = (o: ViewAsOverrides) => setOverridesState(o);
  const reset = () => setOverridesState({});

  return (
    <ViewAsContext.Provider value={{ active, overrides, setOverrides, reset }}>
      {children}
    </ViewAsContext.Provider>
  );
};

export const useViewAs = () => useContext(ViewAsContext);